import {
  BOSS_IDS,
  VESSEL_IDS,
  type BossId,
  type CatalogEntry,
  type VesselId,
} from '@bioforge/protocol';
import { INK, biofilm, boss, doctor, forge, icon, microbe, place, svg, vessel } from './parts.js';

/** Картинки сцен и карточек знакомства — SVG-строки для HTML-оверлеев. */

export const SCENE_WIDTH = 640;
export const SCENE_HEIGHT = 300;

const isBoss = (id: string): id is BossId => (BOSS_IDS as readonly string[]).includes(id);
const isVessel = (id: string): id is VesselId => (VESSEL_IDS as readonly string[]).includes(id);

/** Картинка карточки: микроб, босс, сосуд или значок. */
export function catalogArt(entry: Pick<CatalogEntry, 'id'>): string {
  const id = entry.id;
  const microbeMatch = /^microbe-(\d)$/.exec(id);
  if (microbeMatch) return svg(microbe(Number(microbeMatch[1])));
  if (id === 'biofilm') return svg(biofilm(microbe(2)));
  if (id === 'boss-guards') return svg(biofilm(microbe(0), true));
  const tail = id.replace(/^(boss|vessel)-/, '');
  if (id.startsWith('boss-') && isBoss(tail)) return svg(boss(tail));
  if (id.startsWith('vessel-') && isVessel(tail)) return svg(vessel(tail));
  return svg(icon(id));
}

interface Room {
  readonly wall: readonly [string, string];
  readonly floor: string;
  readonly props: string;
}

const shelf = (y: number) =>
  `<rect x="0" y="${y}" width="${SCENE_WIDTH}" height="10" fill="#4a3426"/>`;
const lamp = (x: number, color: string) =>
  `<circle cx="${x}" cy="26" r="40" fill="${color}" fill-opacity=".18"/><circle cx="${x}" cy="20" r="12" fill="${color}"/>`;

const ROOMS: Record<string, Room> = {
  quarantine: {
    wall: ['#2a0f24', '#0b0716'],
    floor: '#1a1424',
    props: `${lamp(120, '#ff4f7b')}${lamp(520, '#ff4f7b')}<rect x="250" y="120" width="140" height="120" rx="10" fill="#33465a" stroke="${INK}" stroke-width="5"/><path d="M250 120 L230 70 L370 60 L390 120" fill="#4b6075" stroke="${INK}" stroke-width="5"/><path d="M290 150 h60 M290 175 h60" stroke="#ffd23a" stroke-width="10" stroke-dasharray="14 10"/>`,
  },
  pantry: {
    wall: ['#1d2a1f', '#0b0f0c'],
    floor: '#231a14',
    props: `${shelf(110)}${shelf(200)}${[60, 130, 470, 540].map((x, i) => place(vessel('jar', i % 2 === 0), x, 30, 0.38)).join('')}${[90, 500].map((x) => place(vessel('jar'), x, 120, 0.38)).join('')}<ellipse cx="320" cy="250" rx="70" ry="12" fill="#ddd"/><path d="M270 244 h100 l-10 -18 h-80 Z" fill="#e8b46a" stroke="${INK}" stroke-width="3"/><path d="M282 228 h76" stroke="#7bc96a" stroke-width="6"/>`,
  },
  analytic: {
    wall: ['#0d2236', '#070d18'],
    floor: '#142030',
    props: `${lamp(320, '#8cfff0')}<rect x="60" y="170" width="200" height="16" rx="6" fill="#4b6075"/>${[0, 1, 2, 3, 4].map((i) => place(vessel('tube', i % 2 === 0), 60 + i * 38, 70, 0.5)).join('')}`,
  },
  incubator: {
    wall: ['#3a1d0d', '#120904'],
    floor: '#24160c',
    props: [40, 470]
      .map(
        (x) =>
          `<rect x="${x}" y="50" width="130" height="190" rx="14" fill="#5a3a26" stroke="${INK}" stroke-width="5"/><rect x="${x + 16}" y="70" width="98" height="120" rx="8" fill="#ff9a3d" fill-opacity=".45"/>${place(vessel('petri', false), x + 20, 100, 0.45)}${place(vessel('petri'), x + 20, 140, 0.45)}`,
      )
      .join(''),
  },
  chemical: {
    wall: ['#0e2a1c', '#06120c'],
    floor: '#10221a',
    props: `<path d="M0 60 H640 M0 84 H640" stroke="#b87333" stroke-width="10"/>${[30, 110, 500, 570].map((x, i) => place(vessel('flask', i % 2 === 1), x, 120, 0.5)).join('')}${[160, 480].map((x) => `<path d="M${x} 60 q12 -30 0 -50 q-12 -20 0 -40" stroke="#bdf3ff" stroke-opacity=".4" stroke-width="10" fill="none"/>`).join('')}`,
  },
  heart: {
    wall: ['#3a0b12', '#0c0306'],
    floor: '#1c0a0d',
    props: `<circle cx="320" cy="140" r="170" fill="#ff7a3d" fill-opacity=".12"/><path d="M0 40 H640 M0 70 H640" stroke="#b87333" stroke-width="12"/>${[60, 540].map((x) => `<rect x="${x}" y="70" width="40" height="190" fill="#5a3a26" stroke="${INK}" stroke-width="4"/>`).join('')}`,
  },
};

function room(name: keyof typeof ROOMS, scene: string): string {
  const r = ROOMS[name] ?? ROOMS['quarantine'];
  if (!r) return svg(scene, SCENE_WIDTH, SCENE_HEIGHT);
  const gradient = `<defs><linearGradient id="wall-${name}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${r.wall[0]}"/><stop offset="1" stop-color="${r.wall[1]}"/></linearGradient></defs>`;
  return svg(
    `${gradient}<rect width="${SCENE_WIDTH}" height="${SCENE_HEIGHT}" fill="url(#wall-${name})"/><rect y="240" width="${SCENE_WIDTH}" height="60" fill="${r.floor}"/>${r.props}${scene}`,
    SCENE_WIDTH,
    SCENE_HEIGHT,
  );
}

const escaping = [0, 1, 2, 0, 1]
  .map((k, i) => place(microbe(k), 270 + i * 34 - (i % 2) * 20, 30 + (i % 3) * 20, 0.22))
  .join('');

const spores = (x: number, y: number) =>
  [0, 1, 2, 1, 0]
    .map(
      (k, i) =>
        `<circle cx="${x + i * 26}" cy="${y + Math.sin(i) * 14}" r="${8 - i}" fill="${['#9b6bff', '#ff4f7b', '#ffd23a'][k]}" fill-opacity=".8"/>`,
    )
    .join('');

const sparkle = (x: number, y: number) =>
  `<path d="M${x} ${y - 14} L${x + 4} ${y - 4} L${x + 14} ${y} L${x + 4} ${y + 4} L${x} ${y + 14} L${x - 4} ${y + 4} L${x - 14} ${y} L${x - 4} ${y - 4} Z" fill="#fff6c2"/>`;

/** Кадры сцен по идентификатору иллюстрации из `scenes.json`. */
const SCENES: Record<string, () => string> = {
  'ch1-intro-1': () =>
    room(
      'quarantine',
      `${escaping}${place(doctor('surprised'), 20, 100, 1)}${place(forge(true), 480, 100, 0.8)}`,
    ),
  'ch1-intro-2': () => room('pantry', `${place(doctor('determined'), 420, 100, 1, true)}`),
  'boss-spornitsa': () =>
    room(
      'pantry',
      `${place(vessel('jar', false), 170, -10, 1.55)}${place(boss('spornitsa'), 230, 60, 0.9)}${sparkle(200, 60)}${sparkle(460, 100)}`,
    ),
  'ch1-outro': () =>
    room(
      'pantry',
      `<rect x="400" y="30" width="180" height="110" rx="10" fill="#26303f" stroke="${INK}" stroke-width="5"/>${[0, 1, 2, 3].map((i) => `<path d="M412 ${50 + i * 24} h156" stroke="${INK}" stroke-width="6"/>`).join('')}${place(boss('spornitsa'), 440, 40, 0.5)}${spores(300, 110)}${place(doctor('determined'), 30, 100, 1)}`,
    ),
  'ch2-intro': () => room('analytic', `${place(doctor('calm'), 420, 100, 1, true)}`),
  'boss-grippozavr': () =>
    room(
      'analytic',
      `${place(boss('grippozavr'), 330, 20, 1.2)}<rect x="300" y="10" width="300" height="230" rx="18" fill="#bdf3ff" fill-opacity=".08" stroke="#9edcf0" stroke-width="5"/>${place(doctor('determined'), 60, 140, 0.8)}`,
    ),
  'ch2-outro': () =>
    room(
      'analytic',
      `${place(boss('grippozavr'), 380, 120, 0.6)}<g transform="translate(470 110)">${'<path d="M-30 0 q15 -12 30 0 t30 0" stroke="#ffd23a" stroke-width="5" fill="none"/>'}</g>${place(forge(), 30, 100, 0.8)}`,
    ),
  'ch3-intro': () => room('incubator', `${place(doctor('calm'), 220, 100, 1)}`),
  'boss-vibrion': () =>
    room(
      'incubator',
      `${place(vessel('petri', false), 180, 50, 1.4)}${place(boss('vibrion'), 240, 0, 0.9)}${place(doctor('determined'), 20, 140, 0.8)}`,
    ),
  'ch3-outro': () =>
    room(
      'incubator',
      `${place(boss('vibrion'), 420, 60, 0.55, true)}<path d="M360 120 h50 M350 150 h40" stroke="#bdf3ff" stroke-width="5" stroke-linecap="round"/>${place(forge(), 200, 100, 0.8)}`,
    ),
  'ch4-intro': () => room('chemical', `${place(doctor('calm'), 230, 100, 1)}`),
  'boss-tuberkul': () =>
    room(
      'chemical',
      `${place(vessel('flask', false), 210, 10, 1.25)}${place(boss('tuberkul'), 270, 100, 0.55)}${place(doctor('determined'), 450, 140, 0.8, true)}`,
    ),
  'ch4-outro': () =>
    room(
      'chemical',
      `${place(icon('effect-sleep'), 380, 80, 0.8)}${place(forge(true), 220, 110, 0.7)}${place(doctor('surprised'), 30, 100, 1)}`,
    ),
  'ch5-intro': () =>
    room(
      'heart',
      `${place(vessel('bioreactor'), 200, 0, 1.25)}${place(doctor('calm'), 20, 100, 1)}`,
    ),
  'boss-chimera': () =>
    room(
      'heart',
      `${place(vessel('bioreactor', false), 170, -40, 1.5)}${place(boss('chimera'), 220, 30, 1)}${sparkle(160, 60)}${sparkle(490, 80)}`,
    ),
  'ch5-outro-1': () =>
    room(
      'heart',
      `${place(boss('chimera'), 250, 40, 0.7)}${[0, 1, 2, 0, 1, 2]
        .map((k, i) => place(microbe(k), 150 + i * 60, 200 - (i % 2) * 40, 0.2))
        .join('')}${sparkle(230, 40)}${sparkle(420, 60)}${sparkle(330, 220)}`,
    ),
  'ch5-outro-2': () =>
    room(
      'pantry',
      `${place(doctor('calm'), 60, 100, 1)}${place(forge(), 400, 100, 0.8)}<text x="330" y="90" font-family="system-ui, sans-serif" font-weight="800" font-size="64" fill="#ffd23a">?</text>`,
    ),
};

/** Кадр сцены; неизвестная иллюстрация — пустая лаборатория (контент новее клиента). */
export function sceneArt(art: string): string {
  return (SCENES[art] ?? (() => room('quarantine', place(forge(), 240, 100, 0.8))))();
}

/** Иллюстрации, для которых есть кадр (проверяется тестом против контента сервера). */
export const SCENE_ART_IDS: readonly string[] = Object.keys(SCENES);
